import { useCallback, useState } from 'react';
import type { AlertColor } from '@mui/material';

export interface SnackbarState {
  message: string;
  severity: AlertColor;
}

export interface UseSnackbarReturn {
  snackbarState: SnackbarState | null;
  showSnackbar: (message: string, severity?: AlertColor) => void;
  closeSnackbar: () => void;
}

/**
 * Hook for managing snackbar state.
 *
 * @example
 * const { snackbarState, showSnackbar, closeSnackbar } = useSnackbar();
 *
 * // Show success message
 * showSnackbar('Operation completed successfully', 'success');
 *
 * // Show error message
 * showSnackbar('Something went wrong', 'error');
 *
 * // Use with FeedbackSnackbar component
 * <FeedbackSnackbar state={snackbarState} onClose={closeSnackbar} />
 */
export const useSnackbar = (): UseSnackbarReturn => {
  const [snackbarState, setSnackbarState] = useState<SnackbarState | null>(null);

  const showSnackbar = useCallback((message: string, severity: AlertColor = 'success') => {
    setSnackbarState({ message, severity });
  }, []);

  const closeSnackbar = useCallback(() => {
    setSnackbarState(null);
  }, []);

  return {
    snackbarState,
    showSnackbar,
    closeSnackbar,
  };
};

export default useSnackbar;
